/**
 * The operator hotkey table (SPEC AC-OPS-5). Every action listed there gets a default binding
 * here; `setting.ops.hotkeys` (see opsSettings.ts) can remap any of them by action name, which is
 * why bindings are looked up by ACTION and matched against the live keyboard event, never the
 * other way around — two actions can never silently collide on the same key because the operator
 * only ever edits one action's binding at a time (SettingsScreen enforces uniqueness there).
 *
 * SPEC's own decision doc: "Global hotkeys work only while a mv-catalog-player window has focus"
 * (no OS-level global hotkey registration from a browser tab) — the caller (useHotkeys.ts)
 * attaches this to each window's own `keydown`, which is exactly that constraint made concrete.
 */

import type { MessageKey } from '../../i18n/messages.en.ts';
import type { TFunction } from '../../i18n/translate.ts';

export type HotkeyAction =
  | 'playPause'
  | 'next'
  | 'previous'
  | 'stop'
  | 'keyUp'
  | 'keyDown'
  | 'keyReset'
  | 'tempoUp'
  | 'tempoDown'
  | 'offsetDown'
  | 'offsetUp'
  | 'mute'
  | 'breakMusic'
  | 'fullscreen'
  | 'toggleLyrics'
  | 'search'
  | 'shortcutSheet'
  | 'undo'
  | 'acceptTopRequest'
  /* MVP-679: leave storefront mode from the keyboard. The one action in this table that is NOT
     about the show — see `DEFAULT_HOTKEYS` for why it carries two modifiers. */
  | 'exitStorefront';

export const DEFAULT_HOTKEYS: Record<HotkeyAction, string> = {
  playPause: ' ',
  next: 'n',
  previous: 'p',
  stop: 's',
  keyUp: '+',
  keyDown: '-',
  keyReset: '0',
  tempoUp: 'ArrowUp',
  tempoDown: 'ArrowDown',
  offsetDown: '[',
  offsetUp: ']',
  mute: 'm',
  breakMusic: 'b',
  fullscreen: 'f',
  toggleLyrics: 'l',
  search: '/',
  shortcutSheet: '?',
  undo: 'Ctrl+z',
  /* MVP-679, owner: "ควรจะกดออกจากโหมดหน้าร้านด้วยคีย์ลัดได้".
   *
   * MVP-500 gave this mode no accelerator on purpose — "the way in is a button and the way out is a
   * button" — because the room PC is standing in front of strangers and the mode's whole job is to
   * survive them. Two modifiers and a letter on the far edge of the keyboard is the compromise the
   * owner's ask allows: nothing a sleeve, a lean or a curious single keypress can produce, and
   * still one motion for a KJ who knows it. It is deliberately NOT a bare letter like the rest of
   * this table, and deliberately not on the eXtreme preset's remapped row either.
   *
   * When MVP-678 lands, this key opens the passcode prompt rather than exiting directly — the
   * shortcut is a way to REACH the exit, not a second exit that skips the lock. */
  exitStorefront: 'Ctrl+Shift+x',
  // MVP-547: the request tray had no key at all — the screen was exhaustively keyboarded for
  // transport and rotation and entirely mouse-bound for the one thing a paying guest is waiting on.
  // `a` for accept; only ACCEPT gets a key. Reject tells someone in the room no, and a key that
  // does that from across the bar is not a convenience. See `acceleratorTarget` in
  // `features/requests/requestTray.ts` for which row it lands on.
  acceptTopRequest: 'a',
};

/** Order the shortcut sheet (AC-OPS-5) lists them in — grouped roughly by what they control. */
export const HOTKEY_ORDER: HotkeyAction[] = [
  'playPause',
  'next',
  'previous',
  'stop',
  'keyUp',
  'keyDown',
  'keyReset',
  'tempoUp',
  'tempoDown',
  'offsetDown',
  'offsetUp',
  'mute',
  'breakMusic',
  'toggleLyrics',
  'fullscreen',
  'search',
  'shortcutSheet',
  'undo',
  // MVP-547: last, and on its own — it is the only key here that acts on the request tray rather
  // than on playback, so grouping it beside `next` would read as transport.
  'acceptTopRequest',
  /* MVP-679: last, after the tray key, because it is the only action here that acts on the MACHINE
     rather than on the show. Listed at all — rather than kept as an undocumented chord — because
     this repo's own rule is that a key which does something is a key the sheet names; a KJ who
     cannot find the way out is the failure mode `storefrontMode.ts` calls "a worse night". */
  'exitStorefront',
];

/** MVP-11 #7: the Cockpit hotkeys actually wired to a handler (OperatorConsole.tsx's
 * `hotkeyHandlers`) — every `HOTKEY_ORDER` action except `search` (the Cockpit has no search
 * field; only Library wires that key, through its own keydown handler, never `useHotkeys`).
 * Exported once so both `ShortcutSheet` callers (OperatorConsole, LibraryScreen) pass the same
 * "actions" list to `consoleShortcutSections` — the Cockpit section then never advertises a key
 * neither screen actually handles. */
export const WIRED_COCKPIT_ACTIONS: HotkeyAction[] = HOTKEY_ORDER.filter((action) => action !== 'search');

/** MVP-272 Phase 3c-3: what each action is CALLED, on the shortcut sheet and in Settings >
 * Shortcuts. This module is imported by `hotkeys.test.ts` under node with no `I18nProvider`, so it
 * names the dictionary entry and the two `.tsx` render sites do the lookup.
 *
 * MVP-11 #25 still applies to the English values in the dictionary: the glossary is Library /
 * Cockpit / Stage — never "audience window". */
export const HOTKEY_LABEL_KEY: Record<HotkeyAction, MessageKey> = {
  playPause: 'ops.hotkey.playPause',
  next: 'ops.hotkey.next',
  previous: 'ops.hotkey.previous',
  stop: 'ops.hotkey.stop',
  keyUp: 'ops.hotkey.keyUp',
  keyDown: 'ops.hotkey.keyDown',
  keyReset: 'ops.hotkey.keyReset',
  tempoUp: 'ops.hotkey.tempoUp',
  tempoDown: 'ops.hotkey.tempoDown',
  offsetDown: 'ops.hotkey.offsetDown',
  offsetUp: 'ops.hotkey.offsetUp',
  mute: 'ops.hotkey.mute',
  breakMusic: 'ops.hotkey.breakMusic',
  fullscreen: 'ops.hotkey.fullscreen',
  toggleLyrics: 'ops.hotkey.toggleLyrics',
  search: 'ops.hotkey.search',
  shortcutSheet: 'ops.hotkey.shortcutSheet',
  undo: 'ops.hotkey.undo',
  acceptTopRequest: 'ops.hotkey.acceptTopRequest',
  exitStorefront: 'ops.hotkey.exitStorefront',
};

/**
 * MVP-474. A SECOND key that also fires an action, on top of whatever `DEFAULT_HOTKEYS` (or the
 * operator's own remap) says. `+`/`-` are already muscle memory for the KJs using this, so the
 * arrows asked for were added beside them rather than in place of them.
 *
 * Deliberately NOT part of the remappable table: an action has one binding the operator owns and
 * edits, and this is a fixed alias underneath it. `resolveHotkeyAction` checks every PRIMARY
 * binding before it looks here, so remapping any action onto an arrow silently takes that arrow —
 * the operator's own choice always wins over the alias.
 *
 * Tempo is not in this map: it had no hotkey at all before this task, so ↑/↓ are its primaries and
 * stay remappable like everything else.
 */
export const SECONDARY_HOTKEYS: Partial<Record<HotkeyAction, string>> = {
  keyUp: 'ArrowRight',
  keyDown: 'ArrowLeft',
};

/**
 * MVP-500 Commit B. The actions storefront mode refuses: the ones that destroy data or change
 * configuration, and nothing else. A customer leaning on the keyboard at 1am must not be able to
 * cost the venue catalogue rows; the KJ's transport keys are not this set's business, so `next`,
 * `playPause` and the rest stay live while the mode is on.
 *
 * ONE exported set, rather than a guard inside each destructive handler, so the whole policy can be
 * read — and tested with no DOM — in a single place instead of being reconstructed by grepping
 * eighteen handlers.
 *
 * The lock is deliberately NOT expressed by leaving the action UNBOUND. `useHotkeys.onKeyDown`
 * returns early on `if (!action) return` — BEFORE `event.preventDefault()` — because the binding
 * table is the app's claim on a key and the app eats only what it claims (MVP-495). So an unbound
 * `Ctrl+Z` would be handed straight back to the browser and to every other `keydown` listener on
 * the page: the opposite of locked. The refusal therefore happens after the key has been claimed
 * and killed. See `useHotkeys.ts` for the exact ordering, which is load-bearing.
 */
export const STOREFRONT_LOCKED_ACTIONS: ReadonlySet<HotkeyAction> = new Set(['undo']);

/** MVP-500 follow-up: the actions the shortcut sheet may list WHILE THE MODE IS ON — the wired
 * Cockpit list minus whatever the lock refuses. Derived from the same two constants the runtime
 * obeys, never written out by hand, so locking a second action tomorrow removes its sheet row in
 * the same edit.
 *
 * OMITTED, not greyed out: `lib/fullscreen.test.ts` records the house rule that a control which
 * cannot work for as long as this state lasts reads, disabled, as a temporary problem the operator
 * could fix — and Commit B already renders the Library delete button absent for exactly that
 * reason. A sheet row is a smaller claim but the same lie: a KJ who reads "ย้อนกลับ Ctrl+Z",
 * presses it and gets nothing stops trusting the rest of the sheet, which is the one thing making
 * this mode discoverable. */
export const STOREFRONT_SHEET_ACTIONS: HotkeyAction[] = WIRED_COCKPIT_ACTIONS.filter(
  (action) => !STOREFRONT_LOCKED_ACTIONS.has(action)
);

/** Which list a `ShortcutSheet` should advertise. Both sheet call sites (OperatorConsole,
 * LibraryScreen) go through this rather than writing the ternary twice — the two screens must not
 * describe the same keyboard differently. */
export function sheetActions(storefront: boolean): HotkeyAction[] {
  return storefront ? STOREFRONT_SHEET_ACTIONS : WIRED_COCKPIT_ACTIONS;
}

/** Whether `action` is refused right now. `storefront` false answers false for everything — an
 * app that never turns the mode on behaves exactly as it did before this set existed. */
export function isHotkeyLocked(action: HotkeyAction, storefront: boolean): boolean {
  return storefront && STOREFRONT_LOCKED_ACTIONS.has(action);
}

interface MinimalKeyboardEvent {
  key: string;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

/** MVP-11 #7/#8 — a binding whose key IS the plus sign ("+", "ctrl++") must not be torn apart by
 * the separator: split on every "+" except a trailing one. */
export function splitBinding(binding: string): string[] {
  if (binding.endsWith('+')) return [...binding.slice(0, -1).split('+').filter(Boolean), '+'];
  return binding.split('+');
}

/** "Ctrl+Z" -> { key:'z', ctrl:true, shift:false, alt:false, meta:false }. Case-insensitive on
 * the key itself (KeyboardEvent.key is already case-sensitive for letters, e.g. "Z" with Shift). */
function parseBinding(binding: string): { key: string; ctrl: boolean; shift: boolean; alt: boolean; meta: boolean } {
  const parts = splitBinding(binding);
  const key = (parts.pop() ?? '').toLowerCase();
  const modifiers = new Set(parts.map((p) => p.toLowerCase()));
  return {
    key,
    ctrl: modifiers.has('ctrl'),
    shift: modifiers.has('shift'),
    alt: modifiers.has('alt'),
    meta: modifiers.has('meta') || modifiers.has('cmd'),
  };
}

/** MVP-11 #2 — a single printable non-letter key (e.g. "?", "+") only reaches `KeyboardEvent.key`
 * with `shiftKey:true` on a real keyboard (it lives on the shifted row of its physical key); a
 * binding that names the shifted character itself, with no explicit `shift` modifier, means "this
 * character" and must not also demand `shiftKey === false` — that combination can never fire from a
 * real keydown. Letters are excluded: `shift: false` there still means the literal lowercase key. */
function ignoresShift(key: string): boolean {
  return key.length === 1 && !/[a-z]/.test(key);
}

export function matchesBinding(event: MinimalKeyboardEvent, binding: string): boolean {
  const parsed = parseBinding(binding);
  const shiftMatches = parsed.shift ? event.shiftKey === true : ignoresShift(parsed.key) || event.shiftKey === false;
  return (
    event.key.toLowerCase() === parsed.key &&
    event.ctrlKey === parsed.ctrl &&
    shiftMatches &&
    event.altKey === parsed.alt &&
    event.metaKey === parsed.meta
  );
}

/** Human-readable label for a binding string, for the ShortcutSheet and Settings > Shortcuts.
 *
 * MVP-272: deliberately NOT translated. Every string this returns is the name printed on a physical
 * key — "Space", "Ctrl", "N" — and the KJ is looking at that keyboard while reading it. Thai
 * keyboards carry the same Latin keycaps, so translating them would name a key the operator cannot
 * find. The same rule covers `LIBRARY_SHORTCUT_ITEMS`' `keys` arrays ("Enter", "Esc", "Page Up"). */
export function formatBindingLabel(binding: string): string {
  if (binding === ' ') return 'Space';
  return splitBinding(binding)
    .map((part) => ARROW_GLYPH[part] ?? (part.length === 1 ? part.toUpperCase() : part[0].toUpperCase() + part.slice(1)))
    .join(' + ');
}

/** MVP-474. The arrow keys are the one case where the keycap is a SHAPE, not a word — "ArrowRight"
 * is a DOM string, and no keyboard has it printed on it. Same rule as the rest of this function
 * (name what is on the key), which is why the glyph is not translated either. */
const ARROW_GLYPH: Record<string, string> = {
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};

/** MVP-511. The keyboard event a fixed alias would arrive as, so the sheet can ask the SAME
 * question the runtime asks (`matchesBinding`) instead of comparing binding strings by hand —
 * "ArrowRight" vs "arrowright" vs "Shift+ArrowRight" are three strings and one key.
 *
 * `shiftKey` follows `ignoresShift`: an alias that is a single shifted-row character ("+", "*")
 * only ever reaches a real keydown with `shiftKey:true`, and fabricating false for it would make
 * this check miss a primary that genuinely owns that key. */
function aliasEvent(alias: string): MinimalKeyboardEvent {
  const parts = splitBinding(alias);
  const key = (parts.pop() ?? '').toLowerCase();
  const modifiers = new Set(parts.map((p) => p.toLowerCase()));
  return {
    key,
    ctrlKey: modifiers.has('ctrl'),
    shiftKey: modifiers.has('shift') || ignoresShift(key),
    altKey: modifiers.has('alt'),
    metaKey: modifiers.has('meta') || modifiers.has('cmd'),
  };
}

/** MVP-511. Whether pressing `alias` still reaches its action THROUGH THE ALIAS, given the LIVE
 * binding table. `resolveHotkeyAction` checks every primary before any secondary, so a primary
 * that claims this key takes it: the alias is dead and the sheet must not print it.
 *
 * Deliberately over the whole `HOTKEY_ORDER`, not just the actions the sheet is listing — a key
 * taken by an action this sheet hides (storefront's locked `undo`, the Cockpit's unwired `search`)
 * is still taken at runtime, and still not doing what the row claims.
 *
 * The action's OWN primary landing on its alias also returns false: the key works, but it is
 * already the row's first cap, and "→ / →" is not a second way to do anything. */
function aliasIsLive(alias: string, bindings: Record<HotkeyAction, string>): boolean {
  const event = aliasEvent(alias);
  return !HOTKEY_ORDER.some((other) => matchesBinding(event, bindings[other]));
}

/** Resolves a keyboard event to the one action bound to it, given the effective (merged) binding
 * table — or null if nothing matches. First match wins; callers should already guard against two
 * actions sharing a binding (SettingsScreen does, at edit time). */
export function resolveHotkeyAction(event: MinimalKeyboardEvent, bindings: Record<HotkeyAction, string>): HotkeyAction | null {
  for (const action of HOTKEY_ORDER) {
    if (matchesBinding(event, bindings[action])) return action;
  }
  // MVP-474: only after every primary binding has had its say — see SECONDARY_HOTKEYS.
  for (const action of HOTKEY_ORDER) {
    const secondary = SECONDARY_HOTKEYS[action];
    if (secondary && matchesBinding(event, secondary)) return action;
  }
  return null;
}

/** MVP-496: the one definition now lives in `lib/isTypingTarget.ts`, because the guest surface needs
 * the same predicate and must not pay for this hotkey table to get it. Re-exported here so every
 * existing operator-side caller (`useHotkeys`, `App.tsx`, `consoleSearchFocus`) keeps its import. */
export { isTypingTarget } from '../../lib/isTypingTarget.ts';

/** MVP-11 #7 — ShortcutSheet sections from the live binding table. `actions` defaults to
 * HOTKEY_ORDER; callers pass only the actions they actually handle so the sheet never
 * advertises a dead key.
 *
 * MVP-272 Phase 3c-3: `t` is passed IN rather than this returning message keys for the caller to
 * resolve. The return value is a nested structure (sections of items), and both render sites would
 * otherwise have to walk it identically just to swap one field — the same duplication this module
 * exists to prevent. Taking the dictionary reader as an argument keeps the file free of React and
 * of JSX, which is all `node`/vitest needs; the tests pass an identity `t` and assert on keys.
 *
 * MVP-500 Commit C: `labelKeys` overrides the label of individual actions for the duration of a
 * preset — storefront mode uses it to say, on the sheet, which keys a KJ arriving from eXtreme
 * will find do something else here (`extremePreset.ts`'s `EXTREME_LABEL_KEY`). Sparse and
 * defaulted: an action not listed keeps `HOTKEY_LABEL_KEY`, and every existing caller is
 * unchanged. */
export function hotkeySections(
  bindings: Record<HotkeyAction, string>,
  t: TFunction,
  actions: HotkeyAction[] = HOTKEY_ORDER,
  labelKeys: Partial<Record<HotkeyAction, MessageKey>> = {}
): Array<{ title: string; items: Array<{ keys: string[]; description: string; separator?: '+' | '/' }> }> {
  const wanted = new Set(actions);
  return [
    {
      title: t('ops.shortcuts.sectionCockpit'),
      items: HOTKEY_ORDER.filter((a) => wanted.has(a)).map((a) => {
        const secondary = SECONDARY_HOTKEYS[a];
        // MVP-474: an action with an alias lists the two keys as ALTERNATIVES (`separator: '/'`),
        // and collapses a chord primary into one cap so the row cannot read as "Ctrl / Z / →".
        //
        // MVP-511: ...but only while the alias still fires. Checked against the bindings PASSED IN,
        // never `DEFAULT_HOTKEYS`: no default binding collides with an alias, so the defaults are
        // the one table where this question can only answer yes, and a venue's remap is the only
        // way the bug appears at all. The row then falls through and renders its primary alone —
        // OMITTED, not greyed out, the same rule MVP-500 applied to the locked `undo` row: a cap
        // the KJ presses and nothing happens costs the whole sheet its credibility. The primary is
        // never dropped, contested or not — a primary outranks every alias, so it cannot be stolen.
        if (secondary && aliasIsLive(secondary, bindings)) {
          return {
            keys: [formatBindingLabel(bindings[a]), formatBindingLabel(secondary)],
            description: t(labelKeys[a] ?? HOTKEY_LABEL_KEY[a]),
            separator: '/' as const,
          };
        }
        return {
          // MVP-11 #7 — a bare " " (the Space binding) renders as a blank `<Kbd>`; splitBinding
          // already handles the literal "+" key (keyUp), this is Space's own equivalent.
          keys: splitBinding(bindings[a]).map((key) => (key === ' ' ? 'Space' : ARROW_GLYPH[key] ?? key)),
          description: t(labelKeys[a] ?? HOTKEY_LABEL_KEY[a]),
        };
      }),
    },
  ];
}

/** `setting.ops.hotkeys` (SPEC AC-OPS-10: "all remappable") stores only the actions the operator
 * has actually remapped — this is the one place that sparse override map is combined with
 * `DEFAULT_HOTKEYS` into the full table every consumer (`useHotkeys`, `ShortcutSheet`) needs. An
 * override for an action that no longer exists (an older export, a future downgrade) is silently
 * ignored rather than polluting the effective table with a dead key. */
export function effectiveHotkeyBindings(overrides: Record<string, string> | null | undefined): Record<HotkeyAction, string> {
  const merged = { ...DEFAULT_HOTKEYS };
  if (!overrides) return merged;
  for (const action of HOTKEY_ORDER) {
    const override = overrides[action];
    if (typeof override === 'string' && override.length > 0) merged[action] = override;
  }
  return merged;
}
